import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { routes } from '../routes'
import { LANGUAGES } from './LanguageSelector'

const SITE = 'https://humansafari.org'
// pages kept out of search results
const NOINDEX: string[] = [routes.reel.path]
// Open Graph locale per language
const OG_LOCALES: Record<string, string> = {
  en: 'en_US',
  uk: 'uk_UA',
  de: 'de_DE',
  fr: 'fr_FR',
  es: 'es_ES',
  da: 'da_DK',
  pl: 'pl_PL',
  it: 'it_IT',
  zh: 'zh_CN',
}

// address of a page in a language: English has no ?lang, the others are reached with ?lang=xx
const pageUrl = (path: string, lang: string) =>
  lang === 'en' ? `${SITE}${path}` : `${SITE}${path}?lang=${lang}`

// finds the head tag matching `selector`, creating it if it's missing, and sets an attribute on it
function setHeadTag(tag: 'meta' | 'link', selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLElement>(selector)
  if (!el) {
    el = document.createElement(tag)
    document.head.appendChild(el)
  }
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value))
}

// Per-page search and share details: title, description, canonical address, language versions
// (hreflang), Open Graph/Twitter text and <html lang>. Texts are in translations under `seo.<page>`,
// keyed like routes.ts. Renders nothing; it updates the <head> when the page or language changes.
export default function Seo() {
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()

  useEffect(() => {
    const page = Object.entries(routes).find(([, route]) => route.path === pathname)?.[0]
    if (!page || !i18n.exists(`seo.${page}.title`)) return

    const lang = i18n.language
    const isPublic = LANGUAGES.some((l) => l.code === lang)
    const title = t(`seo.${page}.title`)
    const description = t(`seo.${page}.description`)
    const url = pageUrl(pathname, isPublic ? lang : 'en')

    document.title = title
    document.documentElement.lang = lang
    setHeadTag('meta', 'meta[name="description"]', { name: 'description', content: description })
    setHeadTag('link', 'link[rel="canonical"]', { rel: 'canonical', href: url })
    setHeadTag('meta', 'meta[name="robots"]', {
      name: 'robots',
      content: NOINDEX.includes(pathname) ? 'noindex' : 'index, follow',
    })
    setHeadTag('meta', 'meta[property="og:title"]', { property: 'og:title', content: title })
    setHeadTag('meta', 'meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    })
    setHeadTag('meta', 'meta[property="og:url"]', { property: 'og:url', content: url })
    setHeadTag('meta', 'meta[property="og:locale"]', {
      property: 'og:locale',
      content: OG_LOCALES[lang] ?? 'en_US',
    })
    setHeadTag('meta', 'meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    setHeadTag('meta', 'meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: description,
    })

    // the same page in every public language, plus x-default for everyone else
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove())
    const alternates = [
      ...LANGUAGES.map((l) => [l.code, pageUrl(pathname, l.code)]),
      ['x-default', pageUrl(pathname, 'en')],
    ]
    alternates.forEach(([hreflang, href]) => {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = hreflang
      link.href = href
      document.head.appendChild(link)
    })
  }, [pathname, i18n, i18n.language, t])

  return null
}
